import type { ReactNode } from 'react';
import { ChatCircle } from '@phosphor-icons/react';
import type { BusinessDetail } from '@mybusinessmate/schema';
import { initialsOf } from '../../lib/initials';
import { Icon } from '../Icon';
import { IdentityHeader, IdentityTile } from '../shell/identity-header';
import { Chip } from '../ui/chip';
import { StatusTag } from '../ui/status-tag';
import { useToast } from '../ui/toast';
import { MoreButton, pillClass } from '../ui/toolbar-controls';
import { toasts } from '../../lib/toast-presets';

const monthYear = new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/**
 * The header of a business page (Figma A03.2, A05.1): the initials tile, the name, an "Active" chip and the plan, one
 * muted line, and the page's actions. `primary` is the dark action ("New task" on the overview, "New project" on the
 * projects tab).
 * TODO(api): the plan needs billing (Sprint 4) so the chip shows a dash; Open chat needs chat (Sprint 5).
 */
export function BusinessHeader({
  business,
  primary,
}: {
  business: Pick<BusinessDetail, 'name' | 'industry' | 'city' | 'ownerName' | 'createdAt'>;
  primary?: ReactNode;
}) {
  const toast = useToast();
  const soon = () => toast(toasts.comingSoon('This arrives with its own sprint.'));
  return (
    <IdentityHeader
      tile={<IdentityTile>{initialsOf(business.name)}</IdentityTile>}
      title={business.name}
      meta={
        <>
          <StatusTag size="md" label="Active" tone="green" />
          <Chip size="md">Plan · —</Chip>
          <span>
            {[
              business.industry,
              business.city,
              business.ownerName,
              `client since ${monthYear.format(new Date(business.createdAt))}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </>
      }
      actions={
        <>
          <button type="button" onClick={soon} className={pillClass('light', 'lg')}>
            <Icon icon={ChatCircle} size="sm" />
            Open chat
          </button>
          {primary}
          <MoreButton onClick={soon} />
        </>
      }
    />
  );
}
